import {Helmet} from '@dr.pogodin/react-helmet';
import {
  closeCurrentDialog,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {always, isNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {
  useGetArticleUpdateJobQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderFlag,
  getUserName,
  handleApiError,
  logError,
  NavigationItem,
  queryParams,
  usePermissions,
} from '@dms/shared';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
  useQueryState,
} from 'shared/web';

import {Overview} from './(sections)/Overview/Overview';
import {UpdateList} from './(sections)/UpdateList/UpdateList';
import {UpdateResults} from './(sections)/UpdateResults/UpdateResults';
import {PlanExecutionForm} from './components/PlanExecutionForm';

export function UpdateJobDetail() {
  const {id} = useParams();

  const navigate = useNavigate();

  const formatDate = useDateTimeFormatter();

  const [articleUpdateJobEdit] = usePermissions({
    permissionKeys: ['articleUpdateJobEdit'],
  });

  const [activeSectionId, setActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );

  const articleUpdateJobQuery = useGetArticleUpdateJobQuery(
    {articleUpdateJobId: id as string},
    {skip: isNil(id)}
  );

  const headerActionsQuery = useGetWarehouseHeaderDynamicActionsQuery(
    {
      id: id as string,
      resource: 'article-update-job',
    },
    {skip: isNil(id)}
  );

  const createdByUserQuery = useGetUserQuery(
    {id: articleUpdateJobQuery.data?.createdBy as string},
    {skip: isNil(articleUpdateJobQuery.data?.createdBy)}
  );

  const [putHeaderAction, {isLoading: isLoadingHeaderAction}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const isDraft = isNil(id);

  const isCompleted = articleUpdateJobQuery.data?.state === 'COMPLETED';

  const isCancelled = articleUpdateJobQuery.data?.state === 'CANCELLED';

  const isLoading =
    articleUpdateJobQuery.isLoading || headerActionsQuery.isLoading || createdByUserQuery.isLoading;

  const hasError = articleUpdateJobQuery.isError || headerActionsQuery.isError;

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const handleActionClick = (actionKey: string) => {
    if (isNil(id)) {
      return logError('articleUpdateJobId is not defined');
    }

    return (
      match(actionKey)
        .with('cancel', () =>
          openConfirmDialog({
            onConfirm: () =>
              putHeaderAction({id, resource: 'article-update-job', body: {actionKey}})
                .unwrap()
                .then(() =>
                  showNotification.success(i18n.t('general.notifications.successfullyCancelled'))
                )
                .catch(handleApiError),
          })
        )
        // Confirmation is handled centrally by createHeaderActions; returning the
        // promise keeps the delete dialog open with a loading state until it settles
        .with('delete', () =>
          putHeaderAction({id, resource: 'article-update-job', body: {actionKey}})
            .unwrap()
            .then(() =>
              showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
            )
            // Non-empty options force same-tab navigation while the confirm
            // chord's Ctrl/⌘ may still be held (useNavigate would window.open)
            .then(() => navigate(composePath(warehouseRoutes.updateJobsList), {replace: false}))
            .catch(handleApiError)
        )
        .with('plan_execution', () =>
          openDialog(
            <PlanExecutionForm
              onSubmit={(plannedDate) => {
                putHeaderAction({
                  id,
                  resource: 'article-update-job',
                  body: {actionKey: 'plan_execution', plannedDate},
                })
                  .unwrap()
                  .then(() =>
                    showNotification.success(
                      i18n.t('general.notifications.executionPlannedSuccessfully')
                    )
                  )
                  .then(() => closeCurrentDialog())
                  .then(() =>
                    navigate(
                      composePath(warehouseRoutes.updateJobDetailOverview, {
                        params: {id},
                        queryParams: {tab: 'approval-execution-details'},
                      })
                    )
                  )
                  .catch(handleApiError);
              }}
            />,
            {
              title: i18n.t('entity.warehouse.labels.planExecution'),
              size: 'small',
            }
          )
        )
        .otherwise(() =>
          putHeaderAction({id, resource: 'article-update-job', body: {actionKey}})
            .unwrap()
            .catch(handleApiError)
        )
    );
  };

  const createdAt = `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(articleUpdateJobQuery.data?.createdAt)}`;

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUserQuery.data) ?? EMPTY_PLACEHOLDER}`;

  const source = `${i18n.t('general.labels.source')}: ${match(articleUpdateJobQuery.data?.source)
    .with('USER', always(i18n.t('general.labels.user')))
    .with('INTEGRATION', always(i18n.t('general.labels.integration')))
    .otherwise(always(EMPTY_PLACEHOLDER))}`;

  const isDraftFlag: EntityHeaderFlag[] = [
    {
      colorScheme: 'neutral',
      label: i18n.t('general.labels.new'),
    },
  ];

  const headerFlags = match(articleUpdateJobQuery.data?.state)
    .returnType<EntityHeaderFlag[]>()
    .with(
      'NEW',
      always([
        {
          colorScheme: 'neutral',
          label: i18n.t('general.labels.new'),
        },
      ])
    )
    .with(
      'PLANNED',
      always([
        {
          colorScheme: 'blue',
          label: i18n.t('general.labels.planned'),
        },
      ])
    )
    .with(
      'IN_PROGRESS',
      always([
        {
          colorScheme: 'orange',
          label: i18n.t('general.labels.inprogress'),
        },
      ])
    )
    .with(
      'COMPLETED',
      always([
        {
          colorScheme: 'green',
          label: i18n.t('general.labels.completed'),
        },
      ])
    )
    .with(
      'CANCELLED',
      always([
        {
          colorScheme: 'red',
          label: i18n.t('general.labels.canceled'),
        },
      ])
    )
    .otherwise(always([]));

  const headerActions = createHeaderActions({
    actions: headerActionsQuery.data?.actions,
    callback: handleActionClick,
    isLoading: isLoadingHeaderAction,
  });

  const header: DetailTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.updateArticlesJob'),
    isEntityLoading: articleUpdateJobQuery.isFetching,
    icon: 'action/update',
    parameters: [createdAt, createdBy, source],
    flags: isDraft ? isDraftFlag : headerFlags,
    actions: headerActions,
    alerts: !articleUpdateJobEdit
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingArticleUpdateJobEditPermission'),
          },
        ]
      : undefined,
  };

  const navigation = buildArray<NavigationItem>()
    .add({
      id: 'overview',
      label: i18n.t('page.warehouse.labels.overview'),
      href: isDraft
        ? warehouseRoutes.updateJobCreate
        : composePath(warehouseRoutes.updateJobDetailOverview, {params: {id}}),
      content: (
        <Overview
          isDraft={isDraft}
          articleUpdateJob={articleUpdateJobQuery.data}
          data-testid={testIds.warehouse.updateJobDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.updateJobDetailOverview('navigation.overview'),
    })
    .whenNot(isDraft, {
      id: 'update-list',
      label: i18n.t('page.warehouse.labels.updateList'),
      href: composePath(warehouseRoutes.updateJobDetailUpdateList, {params: {id: id ?? ''}}),
      content: (
        <UpdateList
          isEditingDisabled={isCompleted || isCancelled}
          articleUpdateJobId={id as string}
          data-testid={testIds.warehouse.updateJobDetailUpdateList('section.updateList')}
        />
      ),
      'data-testid': testIds.warehouse.updateJobDetailOverview('navigation.updateList'),
    })
    .whenNot(isDraft, {
      id: 'update-results',
      label: i18n.t('page.warehouse.labels.updateResults'),
      href: composePath(warehouseRoutes.updateJobDetailUpdateResults, {params: {id: id ?? ''}}),
      content: (
        <UpdateResults
          articleUpdateJobId={id as string}
          data-testid={testIds.warehouse.updateJobDetailUpdateResults('section.updateResults')}
        />
      ),
      'data-testid': testIds.warehouse.updateJobDetailOverview('navigation.updateResults'),
    });

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.updateJobDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(articleUpdateJobQuery.data)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isLoadingHeaderAction}
        header={header}
        navigation={navigation}
        activeSectionId={activeSectionId}
        onSectionChange={setActiveSectionId}
        data-testid={testIds.warehouse.updateJobDetailOverview('page')}
      />
    </>
  );
}
